import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import Flex from '../../../common/Flex';
import CardDropdown from '../../../common/CardDropdown';
import { list as listCompetitions } from '../../../../lib/api/competition';
import {
  itemCompetitionRound,
  listCompetitionRounds,
  removeCompetitionRound
} from '../../../../lib/api/match';
import AddRound from './AddRound';
import { Dropdown } from 'react-bootstrap';
import EditRound from './EditRound';
import { toast } from 'react-toastify';

export const RoundSchedule = () => {
  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetitionId, setSelectedCompetitionId] = useState('');
  const [items, setItems] = useState([]);
  const [loadingCompetitions, setLoadingCompetitions] = useState(false);
  const [loadingRounds, setLoadingRounds] = useState(false);
  const [editLoadingGuid, setEditLoadingGuid] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRound, setSelectedRound] = useState(null);

  useEffect(() => {
    const fetchCompetitions = async () => {
      setLoadingCompetitions(true);
      try {
        const response = await listCompetitions();
        setCompetitions(response || []);

        if (response?.length) {
          setSelectedCompetitionId(String(response[0].competitions_id));
        }
      } catch (error) {
        console.error('Greska prilikom ucitavanja takmicenja:', error);
      } finally {
        setLoadingCompetitions(false);
      }
    };

    fetchCompetitions();
  }, []);

  const fetchRounds = async (competitionId, showLoader = true) => {
    if (!competitionId) {
      setItems([]);
      return;
    }

    if (showLoader) setLoadingRounds(true);
    try {
      const response = await listCompetitionRounds({
        competitions_id: competitionId
      });
      setItems(response || []);
    } catch (error) {
      console.error('Greska prilikom ucitavanja kola:', error);
      setItems([]);
    } finally {
      if (showLoader) setLoadingRounds(false);
    }
  };

  useEffect(() => {
    fetchRounds(selectedCompetitionId);
  }, [selectedCompetitionId]);

  const selectedCompetition = useMemo(
    () =>
      competitions.find(
        item => String(item.competitions_id) === String(selectedCompetitionId)
      ) || null,
    [competitions, selectedCompetitionId]
  );

  const canAddRound =
    selectedCompetition &&
    ![2, 3].includes(Number(selectedCompetition.competition_system_type_id));

  const handleEdit = async competitionRoundGuid => {
    setEditLoadingGuid(competitionRoundGuid);
    try {
      const response = await itemCompetitionRound({
        competition_round_guid: competitionRoundGuid
      });
      const roundItem = Array.isArray(response) ? response[0] : response;
      setSelectedRound(roundItem);
      setShowEditModal(true);
    } catch (error) {
      console.error('Greska prilikom ucitavanja kola za izmjenu:', error);
    } finally {
      setEditLoadingGuid(null);
    }
  };

  const handleDelete = async competitionRoundGuid => {
    if (!confirm('Da li ste sigurni da želite obrisati kolo?')) {
      return;
    }

    setLoadingRounds(true);
    try {
      await removeCompetitionRound(competitionRoundGuid);
      toast.success('Kolo je uspješno obrisano.');
      await fetchRounds(selectedCompetitionId);
    } catch (error) {
      console.error('Greska prilikom brisanja kola:', error);
      toast.error(
        error?.body?.message ||
          error?.message ||
          'Došlo je do greške prilikom brisanja.'
      );
    } finally {
      setLoadingRounds(false);
    }
  };

  const renderActions = item => (
    <CardDropdown>
      <div className="py-2">
        <Dropdown.Item
          disabled={editLoadingGuid === item.competition_round_guid}
          onClick={() => handleEdit(item.competition_round_guid)}
        >
          Edit
        </Dropdown.Item>
        {![2, 3].includes(
          Number(selectedCompetition?.competition_system_type_id)
        ) && (
          <>
            <Dropdown.Divider />
            <Dropdown.Item
              className="text-danger"
              onClick={() => handleDelete(item.competition_round_guid)}
            >
              Delete
            </Dropdown.Item>
          </>
        )}
      </div>
    </CardDropdown>
  );

  return (
    <Row className="g-3">
      <Col xs={12}>
        <Card className={classNames('h-100')}>
          <Card.Header>
            <Row className={classNames('g-2')}>
              <Col>
                <Flex>
                  <h5 className="mb-0 hover-actions-trigger text-truncate text-nowrap">
                    Raspored Kola
                  </h5>
                </Flex>
              </Col>
              <Col xs="auto">
                {canAddRound && (
                  <Button onClick={() => setShowAddModal(true)} variant="primary">
                    Novo kolo
                  </Button>
                )}
                {selectedCompetition &&
                  [2, 3].includes(
                    Number(selectedCompetition.competition_system_type_id)
                  ) && (
                    <Link
                      to="/competitions"
                      className="text-600 small fw-semibold text-decoration-none d-inline-block pt-2"
                    >
                      Takmičenje se generiše po Bergeru
                    </Link>
                  )}
              </Col>
            </Row>
          </Card.Header>

          <Card.Body className="bg-body-tertiary">
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="competitions_id">
                  <Form.Label>Takmičenja</Form.Label>
                  <Form.Select
                    value={selectedCompetitionId}
                    onChange={event =>
                      setSelectedCompetitionId(event.target.value)
                    }
                    disabled={loadingCompetitions}
                  >
                    {competitions.map(item => (
                      <option
                        key={item.competitions_id}
                        value={item.competitions_id}
                      >
                        {item.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {loadingCompetitions || loadingRounds ? (
              <div className="text-center">
                <Spinner animation="border" role="status" />
              </div>
            ) : (
              <Row className="mb-3">
                <div className="d-none d-xl-block">
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>Takmičenja</th>
                        <th>Kolo</th>
                        <th>Slobodan klub</th>
                        <th className="text-end">Akcija</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(item => (
                        <tr key={item.competition_round_id}>
                          <td>
                            <div className="ms-2">{item.competitions_name}</div>
                          </td>
                          <td>
                            <div className="ms-2">{item.match_round_name}</div>
                          </td>
                          <td>
                            <div className="ms-2">{item.bye_club_name || ''}</div>
                          </td>
                          <td className="text-end">{renderActions(item)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <div className="d-xl-none">
                  <Row className="g-3">
                    {items.map(item => (
                      <Col xs={12} key={item.competition_round_guid || item.competition_round_id}>
                        <div
                          className="border rounded-4 bg-white h-100 p-3 shadow-sm"
                          style={{ borderColor: '#dfe3ee' }}
                        >
                          <div className="d-flex align-items-start justify-content-between gap-3">
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                              <div className="fw-semibold text-dark fs-8">
                                {item.match_round_name || '-'}
                              </div>
                            </div>
                            <div className="flex-shrink-0">{renderActions(item)}</div>
                          </div>

                          <div className="text-700 fs-10 mt-2">
                            {item.competitions_name || '-'}
                          </div>

                          <div className="text-600 fs-10 mt-1">
                            Slobodan klub: {item.bye_club_name || '-'}
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              </Row>
            )}
          </Card.Body>
        </Card>
      </Col>

      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Novo kolo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AddRound
            competitions={competitions}
            selectedCompetitionId={selectedCompetitionId}
            onClose={() => setShowAddModal(false)}
            onSave={competitionId => fetchRounds(competitionId || selectedCompetitionId)}
          />
        </Modal.Body>
      </Modal>

      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Izmjena kola</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRound && (
            <EditRound
              competitions={competitions}
              initialData={selectedRound}
              onClose={() => setShowEditModal(false)}
              onSave={competitionId =>
                fetchRounds(competitionId || selectedCompetitionId, false)
              }
            />
          )}
        </Modal.Body>
      </Modal>
    </Row>
  );
};

export default RoundSchedule;
